/** What a file a user hands us is, and how big it may be. The web composer, the upload APIs and the agent's manifest share this. */
const MB = 1024 * 1024;
const GB = 1024 * MB;
/** Largest file the resumable upload takes, by kind. */
export const ASSET_MAX_BYTES = {
    video: 20 * GB,
    audio: 20 * GB,
    design: 2 * GB,
    font: 100 * MB,
    image: 2 * GB,
    document: 200 * MB,
    other: 2 * GB,
};
/** Over this a project resource is not copied into the agent's workspace; it reaches the agent as a URL and details. */
export const MAX_RESOURCE_BYTES = 50 * MB;
/** The agent has to read a document, and a storyboard is no use as a link, so a document is copied up to its own limit. */
export const copiedToWorkspace = (kind, size) => size <= (kind === 'document' ? ASSET_MAX_BYTES.document : MAX_RESOURCE_BYTES);
const KIND_BY_EXTENSION = Object.fromEntries([
    ['design', 'psd psb'],
    ['font', 'ttf otf woff woff2'],
    ['video', 'mp4 mov m4v webm mkv avi mxf'],
    ['audio', 'mp3 wav m4a aac flac ogg opus'],
    ['image', 'jpg jpeg png gif webp svg bmp tif tiff heic avif'],
    ['document', 'pdf doc docx ppt pptx txt md rtf odt csv'],
].flatMap(([kind, exts]) => exts.split(' ').map((ext) => [ext, kind])));
const KIND_BY_MIME = [
    [/^video\//, 'video'],
    [/^audio\//, 'audio'],
    [/^font\/|^application\/(x-)?font/, 'font'],
    [/photoshop|^image\/x-psd$/, 'design'],
    [/^image\//, 'image'],
    [/^text\/|^application\/pdf$/, 'document'],
];
export function assetKind(name, mimeType) {
    const dot = name.lastIndexOf('.');
    const byExtension = dot === -1 ? undefined : KIND_BY_EXTENSION[name.slice(dot + 1).toLowerCase()];
    if (byExtension)
        return byExtension;
    const mime = (mimeType ?? '').toLowerCase();
    return KIND_BY_MIME.find(([test]) => test.test(mime))?.[1] ?? 'other';
}
const KIND_LABEL = {
    video: 'Videos',
    audio: 'Audio files',
    design: 'Design files',
    font: 'Fonts',
    image: 'Images',
    document: 'Documents',
    other: 'Files',
};
export function formatFileSize(bytes) {
    if (bytes >= GB)
        return `${(bytes / GB).toFixed(1)} GB`;
    if (bytes >= MB)
        return `${Math.round(bytes / MB)} MB`;
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
const formatLimit = (bytes) => (bytes >= GB ? `${bytes / GB} GB` : `${bytes / MB} MB`);
/** The sentence a person sees when a file is over its kind's limit; null when it fits. */
export function assetLimitMessage(name, mimeType, size) {
    const kind = assetKind(name, mimeType);
    const limit = ASSET_MAX_BYTES[kind];
    return size > limit ? `${name} is ${formatFileSize(size)}. ${KIND_LABEL[kind]} can be up to ${formatLimit(limit)}.` : null;
}
